import React, { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'

import { ModalDialog, Radio, RadioGroup, SecondaryButton, TextButton, Toggle } from '@swaggerhub-portal/component-library'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import Form from '~/components/Form'
import { useNotification } from '~/hooks/useNotification'
import { RoutingOption } from '~/types/api'

export interface RoutingSettingsModalProps {
  isOpen?: boolean;
  initialRouting?: RoutingOption;
  initialCredentialsEnabled?: boolean;
  onConfirm?: (routing: RoutingOption, credentialsEnabled: boolean) => void;
  onClose?: () => void;
  accentColor?: string;
}

export const RoutingSettingsModal = ({
  isOpen = false,
  initialRouting = RoutingOption.BROWSER,
  initialCredentialsEnabled = true,
  onConfirm,
  onClose,
  accentColor,
}: RoutingSettingsModalProps) => {
  const { t } = useTranslation()

  const focusedRadio = useRef<HTMLInputElement>(null)

  const { flashSuccess } = useNotification()

  const [routing, setRouting] = useState<RoutingOption>(initialRouting)
  const [credentialsEnabled, setCredentialsEnabled] = useState(initialCredentialsEnabled)

  const schema = useMemo(() => z.object({
    routing: z.string().default(initialRouting),
    credentialsEnabled: z.boolean().default(initialCredentialsEnabled),
  }), [initialRouting, initialCredentialsEnabled])

  const close = () => {
    onClose?.()
  }

  const submit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    flashSuccess({ message: t('portalStatusCodes.success.2008') })
    onConfirm?.(routing, credentialsEnabled)
    close()
  }

  useEffect(() => {
    focusedRadio.current?.focus()
  }, [])

  return (
    <ModalDialog
      isOpen={isOpen}
      onClose={close}
      translations={{
        title: t('components.ui.apiViewer.routing.modal.title'),
        description: t('components.ui.apiViewer.routing.modal.subtitle'),
      }}
    >
      <Form schema={schema} mode="onChange" onSubmit={submit}>
        {({ Field, formState, register }) => (
          <section className="relative flex flex-col gap-4">
            <RadioGroup className="flex flex-col gap-4 text-body2">
              <Field name="routing" onChange={(event: ChangeEvent<HTMLInputElement>) => setRouting(event.target.value as RoutingOption)}>
                {() => <Radio
                  ref={routing === RoutingOption.BROWSER ? focusedRadio : null}
                  value={RoutingOption.BROWSER}
                  aria-label={t('components.ui.apiViewer.routing.modal.options.browser.label')}
                  id="browser"
                  label={t('components.ui.apiViewer.routing.modal.options.browser.label')}
                  className="checked:!border-consumer-blue dark:checked:!border-dark-consumer-blue"
                  innerElementClassName="bg-consumer-blue dark:bg-dark-consumer-blue"
                />}
              </Field>
              <Field name="credentialsEnabled" className="ml-5 mt-[-4px]">
                {() => <Toggle
                  {...register('credentialsEnabled')}
                  id="credentialsEnabled"
                  name="credentialsEnabled"
                  data-testid="routing-cors-credentials-toggle"
                  onChange={(value: boolean) => setCredentialsEnabled(value)}
                  checked={credentialsEnabled}
                  infoTooltip={t('components.ui.apiViewer.routing.modal.options.browser.credentialsEnabled.tooltip')}
                  className={clsx(credentialsEnabled && 'bg-consumer-blue dark:bg-dark-consumer-blue')}
                  tooltipTriggerInactiveClassName="text-consumer-blue dark:!text-dark-consumer-blue"
                  tooltipTriggerActiveClassName="text-neutral-80 dark:text-neutral-30"
                >
                  {t('components.ui.apiViewer.routing.modal.options.browser.credentialsEnabled.label')}
                </Toggle>}
              </Field>
              <Field name="routing" onChange={(event: ChangeEvent<HTMLInputElement>) => setRouting(event.target.value as RoutingOption)}>
                {() => <Radio
                  ref={routing === RoutingOption.PROXY ? focusedRadio : null}
                  value={RoutingOption.PROXY}
                  aria-label={t('components.ui.apiViewer.routing.modal.options.proxy.label')}
                  id="proxy"
                  label={t('components.ui.apiViewer.routing.modal.options.proxy.label')}
                  className="checked:!border-consumer-blue dark:checked:!border-dark-consumer-blue"
                  innerElementClassName="bg-consumer-blue dark:bg-dark-consumer-blue"
                />}
              </Field>
            </RadioGroup>

            <div className="flex justify-end gap-2">
              <TextButton
                onClick={close}
                size="m"
                className="hover:!bg-neutral-80 hover:!text-neutral-00"
              >
                {t('components.ui.buttons.cancel.label')}
              </TextButton>

              <SecondaryButton
                disabled={!formState.isValid}
                size="m"
                type="submit"
                className={clsx(
                  accentColor && 'text-neutral-98 dark:text-neutral-20 hover:bg-neutral-80 dark:hover:bg-neutral-30 dark:hover:!border-neutral-30 hover:!border-neutral-80 hover:text-neutral-00 dark:hover:text-neutral-98'
                )}
                style={{ borderColor: accentColor }}
              >
                {t('components.ui.buttons.confirm.label')}
              </SecondaryButton>
            </div>
          </section>)}
      </Form>
    </ModalDialog>
  )
}